// ============================================================================
// NewAccountActionCard — every new-account action's card (item #944, the
// new-accounts part), built like the Buildings and Projects cards to Ben's
// approved card demo (docs/build-room/demos/item-944.html): the action and the
// card in the title, the screen's own words, the read-back and a button naming
// the consequence on a card that cannot be undone, Do it at the bottom, every
// control ≥ 44 px, locked once saved. What each action shows is
// newAccountCard() in ../new-account-actions.ts; the save is
// commitNewAccountAction().
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CopilotProposalBase, NewAccountSopStage } from '../../../shared/types';
import { fetchSopCard, previewSopDelete, type SopDeletePreview } from '../../new-account-sop/hooks/use-sop-data';
import { stageMoveCheck, type StageMoveCheck } from '../../new-account-sop/lib/stage-move';
import { newAccountCard, newAccountOf, type NewAccountDraft } from '../new-account-actions';
import { CardShell } from './card-shell';
import { FieldControl } from './building-action-card';

interface Props {
  proposal: CopilotProposalBase;
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

export function NewAccountActionCard({ proposal, isCommitting, onDoIt, onDiscard }: Props) {
  const draft = proposal.input as unknown as NewAccountDraft;
  const start = useMemo(() => ({ ...(draft.fields ?? {}) }), [draft]);
  const [fields, setFields] = useState<Record<string, unknown>>(start);
  // Delete: what happens to the building the card made, read again now — the
  // same answer the delete will act on when Do it is pressed (option C).
  const deleting = draft.action === 'delete_card';
  const [fate, setFate] = useState<SopDeletePreview | null>(null);
  useEffect(() => {
    if (!deleting || proposal.status !== 'Proposed') return;
    let cancelled = false;
    previewSopDelete(draft.sop_id).then((p) => { if (!cancelled) setFate(p); }).catch(() => {});
    return () => { cancelled = true; };
  }, [deleting, proposal.status, draft.sop_id]);
  const shown = fate ? { ...draft, context: { ...(draft.context ?? {}), building: fate } } : draft;
  const card = newAccountCard(shown, fields);
  const wasEdited = JSON.stringify(fields) !== JSON.stringify(start);
  const open = proposal.status === 'Proposed';

  // A stage move: the footer's own rule, on the card as it is now — refused,
  // or the unfinished-checklist question the footer asks before it moves.
  const moving = draft.action === 'move_stage';
  const [check, setCheck] = useState<StageMoveCheck | null>(null);
  useEffect(() => {
    if (!moving || !open) return;
    let cancelled = false;
    fetchSopCard(draft.sop_id)
      .then((row) => {
        if (cancelled) return;
        setCheck(row ? stageMoveCheck(row, String(fields.to) as NewAccountSopStage) : { blocked: 'The card could not be read.', warn: null });
      })
      .catch(() => { if (!cancelled) setCheck({ blocked: 'The card could not be read.', warn: null }); });
    return () => { cancelled = true; };
  }, [moving, open, draft.sop_id, fields.to]);
  const blocked = moving && check?.blocked ? check.blocked : null;
  const waiting = moving && open && check == null;
  const readBack = card.readBack ?? (moving && check?.warn ? check.warn.replace(/\n+/g, ' ') : null);

  return (
    <CardShell
      title={`${card.title} · ${newAccountOf(draft)}`}
      icon={<Building2 className="h-4 w-4" />}
      accentClass={readBack ? 'border-st-warn-border-strong' : 'border-tcs-border'}
      status={proposal.status}
      committedLabel="Saved"
      isCommitting={isCommitting}
      doItDisabled={!!card.missing || !!blocked || waiting}
      doItDisabledReason={card.missing ?? blocked ?? (waiting ? 'Checking the card…' : undefined)}
      readBack={readBack ?? undefined}
      doItLabel={card.doItLabel ?? undefined}
      onDoIt={() => onDoIt({ ...draft, fields }, wasEdited)}
      onDiscard={onDiscard}
      after={
        <Link to={`/new-accounts?card=${encodeURIComponent(draft.sop_id)}`} className="text-sm font-medium text-tcs-text underline">
          Open the card
        </Link>
      }
    >
      {card.info.map((i) => (
        <div key={i.label} className="flex min-h-[44px] items-center justify-between gap-3 rounded-md border border-tcs-border px-2 py-1.5 text-sm">
          <span className="text-xs text-tcs-text-muted">{i.label}</span>
          <span className="text-right text-tcs-text">{i.value}</span>
        </div>
      ))}
      {card.fields.map((spec) => (
        <FieldControl key={spec.key} spec={spec} value={fields[spec.key]} onChange={(v) => setFields((p) => ({ ...p, [spec.key]: v }))} />
      ))}
    </CardShell>
  );
}
